import _ from 'lodash';
import type {
  BizMetricAttributedMap,
  CommsMetricsMap,
  GroupCommsMetrics,
  MetricsGroupMeta,
  Node,
  TridentCampaign,
} from 'types/trident-campaigns';
import type { APIBizMetrics, APICommsMetrics } from 'types/trident-api';
import { nodeTypes } from 'data/trident-types';
import { commsChannels, trackableCommsActions } from 'data/trident-campaigns';
import { findNodes, getDefaultNodeName } from 'omega-rtc/utils/trident-node-utils';
import { getStatsSignificance } from './stats';
import {
  ControlBranchID,
  ControlGroupName,
  GMV_KEY,
} from 'omega-rtc/constants/metrics';
import { CommsMetrics } from 'types/trident-campaigns';
import { numberWithCommas } from 'omega-rtc/utils/metric-utils';
import { METRICS_FIELD_MAPPING } from 'omega-rtc/constants/ucd';

// MetricsGroup is the key for uniquely identifying a group of metrics returned from the get-campaign-metrics API
type MetricsGroup = {
  name: string;
  branchID: string; // for metrics under schedule
  treatmentIDs: Array<number>; // for metrics under scenario
  isExpGroup: boolean; // whether this metrics group is under an experiment group
  isControlGroup: boolean;
  meta: MetricsGroupMeta;
};

// BizMetricAttributedData ...
export type BizMetricAttributedData = {
  data: BizMetricAttributedMap;
  distinctUserCount: number;
  name: string;
  nodeType: string;
  isExpGroup: boolean;
  isControlGroup: boolean;
};

// StatSignifcanceGroup ...
export type StatSignifcanceGroup = {
  name: string;
  mean: number;
  variance: number;
  count: number;
};

// getScheduleCommsMetrics picks the metrics from `allMetrics` that match with any key in `commsGroups`
// returns array of GroupCommsMetrics
export function getScheduleCommsMetrics(scheduleID: number, commsGroups: Array<MetricsGroup>, allMetrics: Array<APICommsMetrics>) {
  const groupMetrcs = commsGroups.map(group => {
    let schMetrics = _.get(
      _.find(allMetrics, m => _.get(m, 'key.scheduleID') === scheduleID && _.get(m, 'key.branchID') === group.branchID),
      'total',
      {}
    );

    schMetrics = processMetrics(schMetrics);

    return {
      name: group.name,
      isExpGroup: group.isExpGroup,
      isControlGroup: group.isControlGroup,
      meta: group.meta,
      metrics: schMetrics,
      analytics: getAnalytics(schMetrics),
    };
  });

  return populateUpliftAnalytics(groupMetrcs);
}

// getScenarioCommsMetrics picks the metrics from `allMetrics` that match with any key in `commsGroups`
// returns array of GroupCommsMetrics
export function getScenarioCommsMetrics(commsGroups: Array<MetricsGroup>, allMetrics: Array<APICommsMetrics>) {
  const groupMetrcs = commsGroups.map(group => {
    let aggMetrics = {};
    _.map(group.treatmentIDs, tmID => {
      const tmMetrics = _.get(
        _.find(allMetrics, m => _.get(m, 'key.treatmentID') === tmID), 'total', {},
      );
      // if we have an action following a chained scenarios node, we can have a comms group with multiple treatment IDs
      // in that case, we aggregate the counts across the treatments
      if (_.isEmpty(aggMetrics)) {
        aggMetrics = tmMetrics;
      } else {
        _.map(tmMetrics, (stats, channel) => {
          aggMetrics[channel] = sumByKey(aggMetrics[channel], stats);
        });
      }
    });

    aggMetrics = processMetrics(aggMetrics);

    return {
      name: group.name,
      isExpGroup: group.isExpGroup,
      isControlGroup: group.isControlGroup,
      meta: group.meta,
      metrics: aggMetrics,
      analytics: getAnalytics(aggMetrics),
    };
  });

  return populateUpliftAnalytics(groupMetrcs);
}

export function extractedBizMetricNodeObject(nodeObject: APIBizMetrics, groupItem: MetricsGroup) {
  const dataObj = _.get(nodeObject, 'data', {});
  const distinctUserCount = _.get(nodeObject, 'userCount', 0);
  _.map(dataObj, (verticalObj, attributedKey) => {
    _.map(verticalObj, (verticalData, verticalKey) => {
      let averageVerticalVal = 0;
      if (distinctUserCount > 0) {
        averageVerticalVal = _.round(_.divide(_.get(verticalData, 'total', 0), distinctUserCount), 3);
      }
      _.set(dataObj, `${attributedKey}.${verticalKey}.average`, averageVerticalVal);
    });
  });

  const bizMetricAttributedData: BizMetricAttributedData = {
    data: dataObj,
    distinctUserCount,
    name: groupItem.name,
    nodeType: '',
    isExpGroup: groupItem.isExpGroup,
    isControlGroup: groupItem.isControlGroup,
  };
  return bizMetricAttributedData;
}

// getBusinessVeritcals ...
export function getBusinessVerticals(metrics: Array<BizMetricAttributedData>) {
  const listOfKeys = [];
  _.map(metrics, (val) => {
    _.map(_.get(val, 'data', {}), (metricTypeObject) => {
      listOfKeys.push(_.keys(metricTypeObject));
    });
  });
  return _.union(...listOfKeys);
}

// getScenarioBusinessMetrics ...
export function getScenarioBusinessMetrics(metricsGroups: Array<MetricsGroup>, allMetrics: Array<APIBizMetrics>) {
  const totalGroupMetrics: Array<BizMetricAttributedData> = [];
  metricsGroups.forEach(groupItem => {
    if (_.isEmpty(groupItem.treatmentIDs)) {
      totalGroupMetrics.push(extractedBizMetricNodeObject({}, groupItem));
    } else {
      _.forEach(groupItem.treatmentIDs, tmID => {
        const tmObject = _.find(allMetrics, m => _.get(m, 'key.treatmentID') === tmID);
        totalGroupMetrics.push(extractedBizMetricNodeObject(tmObject, groupItem));
      });
    }
  });
  return totalGroupMetrics;
}

// getScheduleBusinessMetrics ....
export function getScheduleBusinessMetrics(scheduleID: number, metricsGroups: Array<MetricsGroup>, allMetrics: Array<APIBizMetrics>) {
  const totalGroupMetrics: Array<BizMetricAttributedData> = [];
  metricsGroups.forEach(group => {
    const schObject = _.find(allMetrics, m => _.get(m, 'key.scheduleID') === scheduleID && getScheduleBranchID(Number(_.get(m, 'key.branchID'))) === group.branchID);
    totalGroupMetrics.push(extractedBizMetricNodeObject(schObject, group));
  });
  return totalGroupMetrics;
}

// populateUpliftAnalytics calculates open rate, click-through rate uplift for treatment groups compared to control group with the same channels
// if there are no experiment groups in groupMetrics or control group doesn't have metrics, this function won't make any change
export function populateUpliftAnalytics(groupMetrics: Array<GroupCommsMetrics>) {
  return groupMetrics.map((gm) => {
    if (!_.get(gm, 'isExpGroup', false)) {
      return gm;
    }

    // is exp group, check: 1. this group is not control group, 2. control group exists
    const controlMetrics = _.find(groupMetrics, g => g.isExpGroup && g.isControlGroup);
    if (!controlMetrics || gm.isControlGroup) {
      return gm;
    }

    // for each channel, compare its openRate, clickThroughRate with the corresponding rates in the control group
    // calculate the uplift and store it into the analytics field
    _.map(gm.analytics, (gma, channel) => {
      const controlAnalytics = _.get(controlMetrics, `analytics.${channel}`);
      if (controlAnalytics) {
        const uplift: any = {
          significance: {},
        };
        if (_.has(controlAnalytics, 'openRate')) {
          uplift.openRate = _.get(gma, 'openRate', 0) - _.get(controlAnalytics, 'openRate', 0);
          uplift.significance.openRate = getOpenRateSignificance(
            _.get(controlMetrics, `metrics.${channel}`, {}),
            _.get(gm, `metrics.${channel}`, {}),
            channel,
          );
        }
        if (_.has(controlAnalytics, 'clickThroughRate')) {
          uplift.clickThroughRate = _.get(gma, 'clickThroughRate', 0) - _.get(controlAnalytics, 'clickThroughRate', 0);
          uplift.significance.clickThroughRate = getClickThroughRateSignificance(
            _.get(controlMetrics, `metrics.${channel}`, {}),
            _.get(gm, `metrics.${channel}`, {}),
          );
        }
        _.set(gm, `analytics.${channel}.uplift`, uplift);
      }
    });

    return gm;
  });
}

// getAnalytics derives some analytics from the metrics data
// it returns map: channel -> {openRate, clickThroughRate}
function getAnalytics(metrics: CommsMetricsMap) {
  const analytics = {};
  _.map(metrics, (stats, channel) => {
    analytics[channel] = {
      openRate: safeDivide(
        _.get(stats, 'open', 0),
        _.get(stats, isPushChannel(channel) ? 'sent' : 'delivered', 0)
      ),
    };

    if (!isPushChannel(channel)) {
      _.set(analytics[channel], 'clickThroughRate', safeDivide(_.get(stats, 'click', 0), _.get(stats, 'open', 0)));
    }
  });

  return analytics;
}

function safeDivide(num: number, denom: number) {
  if (denom === 0) {
    return 0;
  }
  return num / denom;
}

// getSchedulesGroupForMetrics finds the groups of comms under the given schedule node
// returns array of {name, branchID}
export function getSchedulesGroupForMetrics(campaign: TridentCampaign, nodeID: string) {
  const expGroupNodes = findNodes(nodeID, campaign.nodes, n => _.get(n, 'type') === nodeTypes.experimentTreatment);
  const startTime = _.get(campaign.nodes, `${nodeID}.data.startTime`);
  if (_.isEmpty(expGroupNodes)) {
    const actionNodes = findNodes(nodeID, campaign.nodes, n => _.get(n, 'type') === nodeTypes.actions); // there should be only one action
    let name = '';
    if (actionNodes.length === 1) {
      name = getCommsGroupNameForActionNode(actionNodes[0], campaign);
    }
    return [{
      name,
      branchID: '',
      meta: {
        hasTrackableComms: isTrackableCommsUsed(_.get(actionNodes, '0', {})),
        isBulkComms: true,
        startTime,
      },
    }];
  }

  const groupPercentages = getExpGroupPercentages(nodeID, campaign.nodes);
  return expGroupNodes.map((node) => {
    const groupID = _.get(node, 'data.groupID');
    const actionNodes = findNodes(node.id, campaign.nodes, n => _.get(n, 'type') === nodeTypes.actions);
    return {
      name: getCommsGroupNameForExpGroup(groupID, groupPercentages),
      branchID: getScheduleBranchID(groupID),
      isControlGroup: groupID === 0,
      isExpGroup: true,
      meta: {
        hasTrackableComms: isTrackableCommsUsed(_.get(actionNodes, '0', {})),
        isBulkComms: true,
        startTime,
      },
    };
  });
}

// getScenarioGroupsForMetrics finds the groups of comms under the given scenarios node
// returns array of {name, treatmentIDs}
export function getScenarioGroupsForMetrics(campaign: TridentCampaign, nodeID: string, isRaw: boolean) {
  const expGroupNodes = findNodes(nodeID, campaign.nodes, n => _.get(n, 'type') === nodeTypes.experimentTreatment);
  const startTime = _.get(campaign.nodes, `${nodeID}.data.startTime`);
  if (_.isEmpty(expGroupNodes)) {
    // there is no experiment in the scenario
    // but there might be condition branches, which lead to multiple action nodes
    const actionNodes = findNodes(nodeID, campaign.nodes, n => _.get(n, 'type') === nodeTypes.actions);
    return actionNodes.map(a => {
      return {
        name: getCommsGroupNameForActionNode(a, campaign),
        treatmentIDs: _.get(campaign, `nodeTreatmentMapping.${a.id}`, []),
        meta: {
          hasTrackableComms: isTrackableCommsUsed(a),
          hasTrigger: hasActionTriggered(campaign, a.id),
          startTime,
        },
      };
    });
  }

  // there are experiment groups, each group can have at most one action
  const groupPercentages = getExpGroupPercentages(nodeID, campaign.nodes);
  return expGroupNodes.map((node) => {
    const groupID = _.get(node, 'data.groupID');
    const actionNodes = findNodes(node.id, campaign.nodes, n => _.get(n, 'type') === nodeTypes.actions); // there should be only one action
    if (_.isEmpty(actionNodes)) { // no-action control group
      return {
        name: getCommsGroupNameForExpGroup(groupID, groupPercentages),
        treatmentIDs: isRaw ? _.get(campaign, `nodeTreatmentMapping.${node.id}`, []) : [],
        isExpGroup: true,
        isControlGroup: groupID === 0,
        meta: {
          hasTrackableComms: false,
          hasTrigger: false,
          startTime,
        },
      };
    }
    return {
      name: getCommsGroupNameForExpGroup(groupID, groupPercentages),
      treatmentIDs: _.get(campaign, `nodeTreatmentMapping.${actionNodes[0].id}`, []),
      isExpGroup: true,
      isControlGroup: groupID === 0,
      meta: {
        hasTrackableComms: isTrackableCommsUsed(actionNodes[0]),
        hasTrigger: hasActionTriggered(campaign, actionNodes[0].id),
        startTime,
      },
    };
  });
}


// object: {"triggered": 100, "sent": 250, ...}. fieldMapper: {"triggered": "totalTriggered", "sent": "totalSent", ...}
export function transformObject(object: any, fieldMapper: any) {
  const targetObject: any = {};
  _.forEach(fieldMapper, (originalField, targetField) => _.set(targetObject, targetField, _.get(object, originalField)));
  return targetObject;
}

// processMetrics finds pushinbox channel in the metrics map, and breaks it into two channels: pciInbox and pciPush
export function processMetrics(metrics: CommsMetricsMap) {
  _.map(metrics, (stats, channel) => {
    if (channel === commsChannels.pushinbox) {
      _.set(metrics, commsChannels.pciPush, transformObject(stats, METRICS_FIELD_MAPPING.pci.push));
      _.set(metrics, commsChannels.pciInbox, transformObject(stats, METRICS_FIELD_MAPPING.pci.inbox));
      _.unset(metrics, channel);
    }
  });
  return metrics;
}

function getCommsGroupNameForActionNode(node: Node, campaign: TridentCampaign) {
  if (!_.isEmpty(node.name)) {
    return node.name;
  }
  return `Action ${getDefaultNodeName(node.id, node.type, _.get(campaign, 'nodesOccMap', {}))}`;
}

function getCommsGroupNameForExpGroup(index: number, groupPercentages: Array<number>) {
  const name = index === 0 ? ControlGroupName : `Group ${index}`;
  return `${name} (${groupPercentages[index]}%)`;
}

function getScheduleBranchID(groupID: number) {
  if (groupID === 0) {
    return ControlBranchID;
  }
  return `Treatment Group ${groupID}`;
}

export function sumByKey(a: any, b: any) {
  const keys = _.union(_.keys(a), _.keys(b));
  const summed: any = {};
  _.map(keys, key => {
    summed[key] = _.get(a, key, 0) + _.get(b, key, 0);
  });
  return summed;
}

// find the experiment node under the root node ID (there can be at most one experiment)
// return the list of percentage values for each experiment group (including control group)
function getExpGroupPercentages(rootNodeID: string, nodes: Array<Node>) {
  const expNodes = findNodes(rootNodeID, nodes, n => _.get(n, 'type') === nodeTypes.experiment);
  const groupPercentages = _.get(expNodes, '[0].data.groupPercentage', []);
  return [100 - _.sum(groupPercentages), ...groupPercentages];
}

export function isTrackableCommsUsed(node: Node) {
  return _.some(node.data, a => _.includes(trackableCommsActions, a.type));
}

export function hasActionTriggered(campaign: TridentCampaign, nodeID: string) {
  return _.get(campaign, `usage.actionUsages.${nodeID}.total`, 0) > 0;
}

// getNumberBase returns the smallest value among the numbers that have the same digits as the input num
// e.g. input: 1~9, output: 1, input: 55, output: 10
export function getNumberBase(num: number) {
  let base = 1;
  while (num >= 10) {
    num /= 10;
    base *= 10;
  }
  return base;
}

// getOpenRateSignificance returns the confidence level (0-1) that A and B's open rate difference is not due to natural randomness
function getOpenRateSignificance(metricsA: CommsMetrics, metricsB: CommsMetrics, channel: string) {
  const sizeA = _.get(metricsA, 'success', 0);
  const sizeB = _.get(metricsB, 'success', 0);

  let convA = _.get(metricsA, 'read', 0);
  let convB = _.get(metricsB, 'read', 0);
  if (channel === commsChannels.push || channel === commsChannels.pciPush) {
    convA = _.get(metricsA, 'open', 0);
    convB = _.get(metricsB, 'open', 0);
  }

  if (sizeA === 0 || sizeB === 0) {
    return 0;
  }
  return getStatsSignificance(sizeA, convA, sizeB, convB);
}

// getClickThroughRateSignificance returns the confidence level (0-1) that A and B's click rate difference is not due to natural randomness
function getClickThroughRateSignificance(metricsA: CommsMetrics, metricsB: CommsMetrics) {
  const sizeA = _.get(metricsA, 'read', 0);
  const sizeB = _.get(metricsB, 'read', 0);
  const convA = _.get(metricsA, 'click', 0);
  const convB = _.get(metricsB, 'click', 0);
  if (sizeA === 0 || sizeB === 0) {
    return 0;
  }
  return getStatsSignificance(sizeA, convA, sizeB, convB);
}


// calculate the proper gap between labels on the y-axis
// 1. find the max value's base, divide it by 10 to get the lower base (e.g. if max value > 100, the lower base will be 10)
// 1. divide the max value by 4
// 2. round the division result by the lower base
// calculateYLabelGap ... for charts
export function calculateYLabelGap(values: Array<number>, maxValLimit: number, gapDivisor: number) {
  const maxVal = _.max(values);
  const lowerBase = maxVal < maxValLimit ? 1 : getNumberBase(maxVal) / maxValLimit;
  const yLabelGap = Math.round(maxVal / gapDivisor / lowerBase) * lowerBase;
  return yLabelGap;
}

// getTotalMetricLabel ...
export const getTotalMetricLabel = (selectedVertical: string, attributedDataKey: string, metricType: string) => {
  return `${_.startCase(`total ${selectedVertical}`)} ${attributedDataKey === GMV_KEY ? 'GMV' : _.capitalize(attributedDataKey)} ${metricType === GMV_KEY ? 'Attributed :' : 'Accumulated :'}`;
};


export function sumFields(objA: any, objB: any, fields: any) {
  const obj: any = {};
  fields.forEach((key: any) => obj[key] = _.get(objA, key, 0) + _.get(objB, key, 0));
  return obj;
}


export function getPercentage(value: number, dividedBy: number) {
  if (_.isUndefined(value) || !_.isNumber(dividedBy) || value === 0 || dividedBy === 0) {
    return 0;
  }
  return ((value / dividedBy) * 100).toFixed(2);
}

export function printPercentage(percentage: number) {
  return (`(${percentage}%)`);
}

export function printNumber(number: any) {
  return _.isNumber(number)
    ? numberWithCommas(number)
    : number;
}

export function isPushChannel(channel: any) {
  return channel === commsChannels.push || channel === commsChannels.pciPush;
}

// getScheduleNotSentMetrics picks the not-sent metrics from `allNotSentMetrics` that match with the scheduleID and branchID in `commsGroups`
// returns array of GroupNotSentMetrics
export function getScheduleNotSentMetrics(scheduleID: number, commsGroups: Array<MetricsGroup>, allNotSentMetrics: Array<any>) {
  const groupMetrics = commsGroups.map(group => {
    const notSentData = _.find(
      allNotSentMetrics,
      m => _.get(m, 'key.scheduleID') === scheduleID && _.get(m, 'key.branchID') === group.branchID
    );

    return {
      name: group.name,
      isExpGroup: group.isExpGroup,
      isControlGroup: group.isControlGroup,
      meta: group.meta,
      details: _.get(notSentData, 'details', []),
      total: _.get(notSentData, 'total', 0),
    };
  });

  return groupMetrics;
}

// getScenarioNotSentMetrics picks the not-sent metrics from `allNotSentMetrics` that match with treatmentIDs in `commsGroups`
// returns array of GroupNotSentMetrics
export function getScenarioNotSentMetrics(commsGroups: Array<MetricsGroup>, allNotSentMetrics: Array<any>) {
  const groupMetrics = commsGroups.map(group => {
    let aggDetails = [];
    let aggTotal = 0;

    _.map(group.treatmentIDs, tmID => {
      const tmNotSentData = _.find(
        allNotSentMetrics,
        m => _.get(m, 'key.treatmentID') === tmID
      );

      if (tmNotSentData) {
        const details = _.get(tmNotSentData, 'details', []);

        // Aggregate details by deliveryDetail
        _.map(details, detail => {
          const existingDetail = _.find(aggDetails, d => d.deliveryDetail === detail.deliveryDetail);
          if (existingDetail) {
            existingDetail.messageCount = (existingDetail.messageCount || 0) + (detail.messageCount || 0);
          } else {
            aggDetails.push({
              deliveryDetail: detail.deliveryDetail,
              messageCount: detail.messageCount || 0,
            });
          }
        });

        aggTotal += _.get(tmNotSentData, 'total', 0);
      }
    });

    return {
      name: group.name,
      isExpGroup: group.isExpGroup,
      isControlGroup: group.isControlGroup,
      meta: group.meta,
      details: aggDetails,
      total: aggTotal,
    };
  });

  return groupMetrics;
}
